<template>
  <div
    ref="root"
    class="place-search"
    :class="{ 'place-search--field': field, 'place-search--open': field && showResults }"
    @click.stop
    @mousedown.stop
    @focusout="onFocusOut"
  >
    <label class="place-search__box">
      <span class="place-search__icon" aria-hidden="true">
        <img v-if="icon" :src="icon" alt="" />
        <span v-else class="material-icons">search</span>
      </span>
      <input
        ref="input"
        v-model="query"
        class="place-search__input"
        type="search"
        autocomplete="off"
        :placeholder="placeholder ?? $t('plan_trail.place_search.placeholder')"
        :aria-label="label ?? placeholder ?? $t('plan_trail.place_search.placeholder')"
        :disabled="disabled"
        @focus="focused = true"
        @keydown.esc.prevent="onEscape"
      />
      <!-- Something at the input's right edge — a "use my location" button, say. -->
      <span v-if="$slots.trailing" class="place-search__trailing" @click.prevent>
        <slot name="trailing" />
      </span>
    </label>
    <div v-if="showResults" class="place-search__results" @mousedown="onResultsMouseDown">
      <template v-if="places.length">
        <p class="place-search__group">{{ $t('plan_trail.place_search.near_route') }}</p>
        <button
          v-for="place in places"
          :key="`${place.category}-${place.id}`"
          type="button"
          class="place-search__result"
          @click="pick({ kind: 'place', place })"
        >
          <span class="place-search__result-name">{{ place.name }}</span>
          <small class="place-search__result-meta">{{ placeTypeLabel(place.type) }} · {{ distanceFrom(place.coordinates) }}</small>
        </button>
      </template>

      <template v-if="googlePlaces && googlePlaces.length">
        <p class="place-search__group">{{ $t('plan_trail.place_search.google_maps') }}</p>
        <button
          v-for="place in googlePlaces"
          :key="place.placeId"
          type="button"
          class="place-search__result"
          :disabled="!!adding"
          @click="pickGoogle(place)"
        >
          <span class="place-search__result-name">{{ place.name }}</span>
          <small v-if="adding === place.placeId" class="place-search__result-meta">{{ $t('plan_trail.place_search.adding') }}</small>
          <small v-else class="place-search__result-meta">{{ googleMeta(place) }}</small>
        </button>
      </template>

      <template v-if="addresses.length">
        <p class="place-search__group">{{ $t('plan_trail.place_search.addresses') }}</p>
        <button
          v-for="address in addresses"
          :key="address.place_id || address.display_name"
          type="button"
          class="place-search__result"
          @click="pickAddress(address)"
        >
          <span class="place-search__result-name">{{ address.display_name }}</span>
        </button>
      </template>

      <p v-if="loadingNearby && !places.length" class="place-search__hint">{{ $t('plan_trail.place_search.searching') }}</p>
      <p v-else-if="nothingFound" class="place-search__hint">
        {{ $t('plan_trail.place_search.no_results') }}
      </p>

      <!-- None of our places is the one they typed: try Google Maps. -->
      <button v-if="offerGoogle" type="button" class="place-search__google" @click="searchGoogle">
        <span class="material-icons" aria-hidden="true">travel_explore</span>
        {{ $t('plan_trail.place_search.search_google', { query: query.trim() }) }}
      </button>
      <p v-if="googleLoading" class="place-search__hint">{{ $t('plan_trail.place_search.google_searching') }}</p>
      <p v-else-if="googleFailed" class="place-search__hint">{{ $t('plan_trail.place_search.google_failed') }}</p>
      <p v-else-if="googlePlaces && !googlePlaces.length" class="place-search__hint">{{ $t('plan_trail.place_search.google_none') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { NearbyPlace, placeTypeLabel, rankNearbyPlaces } from '../services/route-endpoints';
import { GooglePlace, PlacePick, rankGooglePlaces } from '../services/place-picks';
import { importGooglePlace, searchGoogleMaps } from '../services/nearby-places';
import { haversineMeters } from '../services/stage-connectors';
import { stadiaGeocodingService, StadiaGeocodingResult } from '../../../utils/services/StadiaGeocodingService';

/**
 * Search for a place for a route's end or a night's stay: our places near the
 * route first, addresses once a few letters are typed, and — when none of our
 * places matches — Google Maps, on request (each search costs a SerpApi call).
 * Emits `pick` with a PlacePick (see services/place-picks.ts).
 *
 * Two looks: the default panel, opened on demand with its results always
 * showing and the input focused; and a `field`, a plain input that sits in
 * the page and drops its results down only while it's being used (the new
 * plan's Start and End — see PlanTrailGetStarted.vue).
 */

/** Letters typed before addresses and Google Maps are searched. */
const MIN_QUERY = 3;

const props = withDefaults(defineProps<{
  /** [lon, lat] results are ranked by distance from, and searched around. */
  focus: [number, number] | null;
  /** Our places to offer — called once when the panel opens. */
  loadPlaces: () => Promise<NearbyPlace[]>;
  limit?: number;
  variant?: 'panel' | 'field';
  placeholder?: string;
  /** What the input is for, when the placeholder doesn't say (it's the current value). */
  label?: string;
  /** Shown in the input instead of the search icon — a field's marker, say. */
  icon?: string;
  disabled?: boolean;
}>(), { limit: 6, variant: 'panel', placeholder: undefined, label: undefined, icon: undefined, disabled: false });

const emit = defineEmits<{ (event: 'pick', pick: PlacePick): void; (event: 'close'): void }>();

const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const query = ref('');
const focused = ref(false);
const nearby = ref<NearbyPlace[]>([]);
const loadingNearby = ref(true);
const addresses = ref<StadiaGeocodingResult[]>([]);
const googlePlaces = ref<GooglePlace[] | null>(null);
const googleLoading = ref(false);
const googleFailed = ref(false);
/** The Google place being added to ours, once picked. */
const adding = ref<string | null>(null);

const field = computed(() => props.variant === 'field');
const typed = computed(() => query.value.trim().length >= MIN_QUERY);

const places = computed(() => (props.focus ? rankNearbyPlaces(nearby.value, query.value, props.focus, props.limit) : []));

const offerGoogle = computed(() => typed.value
  && !!props.focus
  && !loadingNearby.value
  && places.value.length === 0
  && googlePlaces.value === null
  && !googleLoading.value
  && !googleFailed.value);

/** A field only says nothing was found once enough has been typed to look. */
const nothingFound = computed(() => !places.value.length
  && !addresses.value.length
  && !googlePlaces.value?.length
  && !offerGoogle.value
  && !googleLoading.value
  && (!field.value || typed.value));

/** A field's results drop down while it's in use and there's something to show. */
const showResults = computed(() => !field.value || (focused.value && (places.value.length > 0 || typed.value)));

onMounted(async () => {
  await nextTick();
  if (!field.value) input.value?.focus();
  try {
    nearby.value = await props.loadPlaces();
  } catch {
    nearby.value = [];
  } finally {
    loadingNearby.value = false;
  }
});

function distanceFrom(coordinates: number[]): string {
  if (!props.focus) return '';
  const metres = haversineMeters(props.focus, coordinates);
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(1)} km`;
}

function googleMeta(place: GooglePlace): string {
  return [place.category || placeTypeLabel(place.type), distanceFrom(place.coordinates)].filter(Boolean).join(' · ');
}

let addressTimer: ReturnType<typeof setTimeout> | null = null;
watch(query, (text) => {
  // A new query needs a new Google search.
  googlePlaces.value = null;
  googleFailed.value = false;
  if (addressTimer) clearTimeout(addressTimer);
  if (text.trim().length < MIN_QUERY) {
    addresses.value = [];
    return;
  }
  addressTimer = setTimeout(async () => {
    try {
      addresses.value = await stadiaGeocodingService.autocomplete(text.trim(), props.focus);
    } catch {
      addresses.value = [];
    }
  }, 300);
});
onBeforeUnmount(() => { if (addressTimer) clearTimeout(addressTimer); });

async function searchGoogle() {
  if (!props.focus) return;
  const searched = query.value.trim();
  googleLoading.value = true;
  try {
    const found = await searchGoogleMaps(searched, props.focus);
    // Only if they haven't typed something else meanwhile.
    if (query.value.trim() === searched) {
      googlePlaces.value = rankGooglePlaces(found, props.focus);
    }
  } catch {
    if (query.value.trim() === searched) googleFailed.value = true;
  } finally {
    googleLoading.value = false;
  }
}

/** A field's results close when focus leaves it altogether. */
function onFocusOut(event: FocusEvent) {
  if (!field.value) return;
  const next = event.relatedTarget as Node | null;
  if (next && root.value?.contains(next)) return;
  focused.value = false;
}

/** Choosing a result mustn't blur the field first — the click would never land. */
function onResultsMouseDown(event: MouseEvent) {
  if (field.value) event.preventDefault();
}

function onEscape() {
  if (field.value) {
    focused.value = false;
    input.value?.blur();
  }
  emit('close');
}

function focusInput() {
  input.value?.focus();
}

function pick(choice: PlacePick) {
  if (field.value) {
    // The pick becomes the field's value (its placeholder); the field is done.
    query.value = '';
    focused.value = false;
    input.value?.blur();
  }
  emit('pick', choice);
}

/**
 * A Google Maps place is added to our own places first, so it's picked as
 * one of ours — and stays one for everyone. If it can't be (see
 * importGooglePlace), it's picked as it is.
 */
async function pickGoogle(place: GooglePlace) {
  if (adding.value) return;
  adding.value = place.placeId;
  try {
    const ours = await importGooglePlace(place);
    pick(ours ? { kind: 'place', place: ours } : { kind: 'google', place });
  } finally {
    adding.value = null;
  }
}

function pickAddress(result: StadiaGeocodingResult) {
  pick({ kind: 'address', name: result.display_name, coordinates: [parseFloat(result.lon), parseFloat(result.lat)] });
}

defineExpose({ searchGoogle, pickGoogle, focusInput });
</script>

<style scoped>
.place-search {
  min-width: 0;
  margin-top: 0.25rem;
  padding: 0.5rem;
  border-radius: 10px;
  background: var(--gray-raised, #2A2C34);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* the input */
.place-search__box {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
}

.place-search__icon {
  position: absolute;
  left: 0.7rem;
  display: inline-flex;
  align-items: center;
  color: #8a8d96;
  pointer-events: none;
}

.place-search__icon .material-icons {
  font-size: 18px;
}

.place-search__icon img {
  width: 16px;
  height: 16px;
}

.place-search__input {
  width: 100%;
  min-width: 0;
  padding: 0.55rem 0.75rem 0.55rem 2.3rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: var(--dark, #17181D);
  color: #fff;
  font-size: 0.85rem;
  line-height: 1.3;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.place-search__trailing {
  position: absolute;
  right: 0.35rem;
  display: inline-flex;
  align-items: center;
}

.place-search__box:has(.place-search__trailing) .place-search__input {
  padding-right: 2.4rem;
}

.place-search__input::placeholder {
  color: #8a8d96;
  opacity: 1;
}

.place-search__input:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.22);
}

.place-search__input:focus {
  border-color: rgba(253, 223, 0, 0.7);
  box-shadow: 0 0 0 3px rgba(253, 223, 0, 0.18);
  background: #17181d;
}

.place-search__input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.place-search__input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  cursor: pointer;
  background: center / contain no-repeat
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23a1a4ad'%3E%3Cpath d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm3.5 12.1-1.4 1.4L12 13.4l-2.1 2.1-1.4-1.4 2.1-2.1-2.1-2.1 1.4-1.4 2.1 2.1 2.1-2.1 1.4 1.4-2.1 2.1 2.1 2.1Z'/%3E%3C/svg%3E");
}

/* A field: just the input, in the page; its results float below it. */
.place-search--field {
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: none;
}

.place-search--field .place-search__results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 0.3rem);
  z-index: 20;
  margin: 0;
  padding: 0.5rem;
  border-radius: 10px;
  background: var(--gray-raised, #2A2C34);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.45);
}

.place-search__results {
  max-height: 16rem;
  overflow-y: auto;
  margin-top: 0.35rem;
}

.place-search__group {
  margin: 0.4rem 0 0.15rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a1a4ad;
}

.place-search__result {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.3rem 0.4rem;
  border: none;
  border-radius: 6px;
  background: none;
  color: #fff;
  font-size: 0.8rem;
  text-align: left;
  cursor: pointer;
}

.place-search__result:hover:not(:disabled),
.place-search__result:focus-visible {
  background: rgba(253, 223, 0, 0.12);
}

.place-search__result:disabled {
  cursor: progress;
}

/* One line each: a long address trails off rather than running out of the box. */
.place-search__result-name,
.place-search__result-meta {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.place-search__result-meta {
  color: #a1a4ad;
}

/* Full width and free to wrap: route cards are narrow, and the query in
   the label can be long. */
.place-search__google {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  width: 100%;
  margin-top: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 10px;
  border: 1px solid rgba(253, 223, 0, 0.35);
  background: rgba(253, 223, 0, 0.1);
  color: #fddf00;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
}

.place-search__google .material-icons {
  flex-shrink: 0;
  font-size: 15px;
  line-height: 1.3;
}

.place-search__google:hover {
  background: rgba(253, 223, 0, 0.18);
}

.place-search__hint {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #a1a4ad;
}
</style>
